import React, { useState, useMemo, memo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { evaluateRisk } from '../utils/riskEvaluation';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const COUNTRY_POSITIONS = {
  'Afghanistan': [65, 33], 'Albania': [20, 41], 'Algeria': [3, 28], 'Angola': [17.5, -12.5],
  'Argentina': [-64, -34], 'Australia': [134, -25], 'Austria': [14, 47.5], 'Bangladesh': [90, 24],
  'Belgium': [4.5, 50.8], 'Bolivia': [-65, -17], 'Botswana': [24, -22], 'Brazil': [-52, -10],
  'Burkina Faso': [-1.5, 12.5], 'Burundi': [29.9, -3.4], 'Cambodia': [105, 12.5],
  'Cameroon': [12.5, 6], 'Canada': [-106, 56], 'Chad': [18.7, 15.4], 'Chile': [-71, -35],
  'China': [105, 35], 'Colombia': [-74, 4.5], 'Congo (DRC)': [23.6, -2.9],
  'Congo (Republic)': [15.8, -0.7], "Cote d'Ivoire": [-5.5, 7.5], 'Cuba': [-79.5, 21.5],
  'Czech Republic': [15.5, 49.8], 'Denmark': [10, 56], 'Ecuador': [-78.2, -1.8],
  'Egypt': [30, 27], 'Ethiopia': [39.5, 9], 'Finland': [26, 64], 'France': [2.2, 46.2],
  'Gabon': [11.8, -0.8], 'Germany': [10.5, 51], 'Ghana': [-1.2, 7.9], 'Greece': [22, 39],
  'Guatemala': [-90.2, 15.5], 'Guinea': [-10.9, 10.8], 'Haiti': [-72.3, 19],
  'Honduras': [-86.6, 14.1], 'Hungary': [19, 47.2], 'India': [79, 22], 'Indonesia': [120, -2.5],
  'Iran': [53, 32.5], 'Iraq': [44, 33], 'Ireland': [-8, 53.4], 'Israel': [34.8, 31.5],
  'Italy': [12.6, 42.5], 'Jamaica': [-77.3, 18.1], 'Japan': [138, 36.2], 'Jordan': [36.2, 31],
  'Kazakhstan': [67, 48], 'Kenya': [37.9, -0.02], 'Kuwait': [47.5, 29.5],
  'Laos': [102.5, 18], 'Lebanon': [35.8, 33.9], 'Liberia': [-9.5, 6.4],
  'Libya': [17.2, 27], 'Madagascar': [47, -19], 'Malawi': [34.3, -13.3],
  'Malaysia': [109.5, 4.2], 'Mali': [-2, 17.6], 'Mexico': [-102, 23.6],
  'Mongolia': [103.8, 46.9], 'Morocco': [-6, 32], 'Mozambique': [35.5, -18.7],
  'Myanmar': [96, 19.8], 'Namibia': [17.1, -22.6], 'Nepal': [84.1, 28.4],
  'Netherlands': [5.3, 52.1], 'New Zealand': [174, -41], 'Nicaragua': [-85.2, 12.9],
  'Niger': [9.4, 17.6], 'Nigeria': [8, 9.1], 'North Korea': [127, 40],
  'Norway': [8.5, 61], 'Oman': [57, 21], 'Pakistan': [69, 30.4],
  'Panama': [-80, 8.5], 'Papua New Guinea': [147.2, -6.3], 'Paraguay': [-58.4, -23.4],
  'Peru': [-76, -10], 'Philippines': [122, 13], 'Poland': [19.4, 52],
  'Portugal': [-8.2, 39.4], 'Qatar': [51.2, 25.3], 'Romania': [25, 45.9],
  'Russia': [105, 61], 'Rwanda': [29.9, -2], 'Saudi Arabia': [45, 24],
  'Senegal': [-14.4, 14.5], 'Serbia': [21, 44.2], 'Sierra Leone': [-11.8, 8.5],
  'Singapore': [103.8, 1.4], 'Somalia': [46, 5.2], 'South Africa': [25.5, -29],
  'South Korea': [127.8, 36], 'South Sudan': [30, 7.8], 'Spain': [-3.7, 40.5],
  'Sri Lanka': [80.8, 7.9], 'Sudan': [30, 16], 'Sweden': [15.5, 62],
  'Switzerland': [8.2, 46.8], 'Syria': [38.9, 35], 'Taiwan': [121, 23.7],
  'Tanzania': [34.9, -6.4], 'Thailand': [100.5, 15.9], 'Togo': [1.2, 8.6],
  'Tunisia': [9.5, 34], 'Turkey': [35.2, 39], 'Uganda': [32.3, 1.4],
  'Ukraine': [31.2, 48.4], 'United Arab Emirates': [54, 23.4], 'United Kingdom': [-1.2, 52.5],
  'United States': [-98, 39.8], 'Uruguay': [-56.2, -34.9], 'Uzbekistan': [64.6, 41.4],
  'Venezuela': [-66.6, 7.6], 'Vietnam': [108.3, 16.1], 'Yemen': [48, 15.6],
  'Zambia': [28.3, -13], 'Zimbabwe': [29.2, -19.8], 'Eswatini': [31.5, -26.5],
  'Lesotho': [28.6, -29.6], 'Djibouti': [43, 11.6],
};

// Get the worst risk color from a group of blueprints
const RISK_PRIORITY = { 'HIGH RISK': 0, 'MODERATE': 1, 'CAUTION': 2, 'UNASSESSED': 3, 'STABLE': 4 };

function getWorstRisk(blueprints) {
  let worst = { level: 'UNASSESSED', colorHex: '#9ca3af' };
  for (const bp of blueprints) {
    const risk = evaluateRisk(bp);
    if ((RISK_PRIORITY[risk.level] ?? 5) < (RISK_PRIORITY[worst.level] ?? 5)) {
      worst = risk;
    }
  }
  return worst;
}

const InteractiveMap = memo(function InteractiveMap({ countries, onCountryClick }) {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(null);

  // Group blueprints by country name (case-insensitive)
  const grouped = useMemo(() => {
    const map = {};
    for (const c of countries) {
      const key = c.name.toLowerCase();
      if (!map[key]) map[key] = { name: c.name, blueprints: [] };
      map[key].blueprints.push(c);
    }
    return Object.values(map);
  }, [countries]);

  const handleMarkerClick = (group) => {
    if (group.blueprints.length === 1) {
      onCountryClick(group.blueprints[0]);
    } else {
      setSelectedCountry(selectedCountry?.name === group.name ? null : group);
    }
  };

  return (
    <div className="relative" data-testid="interactive-map">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ scale: 130, center: [15, 5] }}
        style={{ width: '100%', height: 'auto' }}
      >
        <ZoomableGroup zoom={1} minZoom={1} maxZoom={5}>
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => (
                <Geography
                  key={geo.rpiKey || geo.properties?.name || geo.id}
                  geography={geo}
                  fill="#d1d5db"
                  stroke="#f8f9ff"
                  strokeWidth={0.5}
                  style={{
                    default: { fill: '#d1d5db', outline: 'none' },
                    hover: { fill: '#b4c5ff', outline: 'none', cursor: 'pointer' },
                    pressed: { fill: '#93a8e0', outline: 'none' },
                  }}
                />
              ))
            }
          </Geographies>

          {grouped.map((group) => {
            const pos = COUNTRY_POSITIONS[group.name] || COUNTRY_POSITIONS[group.blueprints[0]?.name];
            if (!pos) return null;
            const worstRisk = getWorstRisk(group.blueprints);
            const hasMultiple = group.blueprints.length > 1;

            return (
              <Marker key={group.name} coordinates={pos}>
                <g
                  onClick={() => handleMarkerClick(group)}
                  onMouseEnter={() => setHoveredCountry(group)}
                  onMouseLeave={() => setHoveredCountry(null)}
                  style={{ cursor: 'pointer' }}
                  data-testid={`map-marker-${group.name.toLowerCase()}`}
                >
                  <circle r={10} fill={worstRisk.colorHex} opacity={0.15} />
                  <circle r={5} fill={worstRisk.colorHex} stroke="#fff" strokeWidth={1.5} />
                  {hasMultiple && (
                    <text x={7} y={-4} fontSize={8} fontWeight="bold" fill={worstRisk.colorHex}>{group.blueprints.length}</text>
                  )}
                </g>
              </Marker>
            );
          })}
        </ZoomableGroup>
      </ComposableMap>

      {/* Hover Tooltip — shows all blueprints for this country */}
      {hoveredCountry && !selectedCountry && (
        <div className="absolute top-4 right-4 bg-[#0b1c30] text-white px-4 py-3 rounded-xl shadow-2xl z-30 min-w-[220px] max-w-[300px]"
          data-testid="map-tooltip">
          <p className="text-sm font-bold mb-0.5">{hoveredCountry.name}</p>
          <p className="text-[10px] text-white/40 mb-2">{hoveredCountry.blueprints[0]?.region || 'Unclassified'} — {hoveredCountry.blueprints.length} blueprint{hoveredCountry.blueprints.length > 1 ? 's' : ''}</p>
          <div className="space-y-2">
            {hoveredCountry.blueprints.map((bp) => {
              const risk = evaluateRisk(bp);
              return (
                <div key={bp.id} className="flex items-start gap-2 py-1.5 border-t border-white/10 first:border-0 first:pt-0">
                  <div className="w-2 h-2 rounded-full mt-1 shrink-0" style={{ backgroundColor: risk.colorHex }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-semibold truncate">{bp.sector?.split(' ').slice(0, 3).join(' ') || 'General'}</p>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-[9px] text-white/50">{bp.sections_generated?.length || 0}/31 sections</span>
                      <span className="text-[9px] font-bold" style={{ color: risk.colorHex }}>{risk.level}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Click Selector — for countries with multiple blueprints */}
      {selectedCountry && (
        <div className="absolute top-4 right-4 bg-white text-[#0b1c30] px-1 py-1 rounded-xl shadow-2xl z-40 min-w-[240px] border border-[#c3c6d7]/30"
          data-testid="map-selector">
          <div className="px-3 py-2 border-b border-[#c3c6d7]/20">
            <p className="text-sm font-bold">{selectedCountry.name}</p>
            <p className="text-[10px] text-[#434655]">Select a blueprint to open</p>
          </div>
          {selectedCountry.blueprints.map((bp) => {
            const risk = evaluateRisk(bp);
            return (
              <button key={bp.id}
                onClick={() => { setSelectedCountry(null); onCountryClick(bp); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#eff4ff] rounded-lg transition-all text-left"
                data-testid={`map-select-${bp.id}`}>
                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: risk.colorHex }} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{bp.sector || 'General'}</p>
                  <p className="text-[10px] text-[#434655]">{bp.sections_generated?.length || 0}/31 sections</p>
                </div>
                <span className="text-[9px] font-bold uppercase" style={{ color: risk.colorHex }}>{risk.level}</span>
              </button>
            );
          })}
          <button onClick={() => setSelectedCountry(null)}
            className="w-full text-center text-[10px] text-[#434655] py-1.5 hover:text-[#004ac6]">
            Close
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-5 mt-2">
        {[
          { color: '#ef4444', label: 'High Risk' },
          { color: '#f59e0b', label: 'Moderate' },
          { color: '#3b82f6', label: 'Stable' },
          { color: '#9ca3af', label: 'Unassessed' },
        ].map(l => (
          <div key={l.label} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: l.color }} />
            <span className="text-[10px] text-[#434655] font-bold uppercase tracking-wider">{l.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
});

export default InteractiveMap;
